import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { LogOut, Plus, Edit2, Trash2, X, Box, Package, Mail, Phone, AlertCircle, Settings, Upload, Wrench, Settings as SettingsIcon, Cog, Flame, Drill, Hammer, Zap, Shield, Network, Factory, Boxes, Truck, HardHat, Cpu, Home, Check, LayoutGrid, List, ArrowUpDown, ChevronUp, ChevronDown, Eye, ClipboardList, TrendingUp, Search, Calendar, DollarSign, Target, ExternalLink, FileDown, Key, Bell } from 'lucide-react';
import { 
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle 
} from '@/components/ui/alert-dialog';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast, Toaster } from 'sonner';
import DragDropImageUpload from '../components/DragDropImageUpload';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL !== "undefined") ? process.env.REACT_APP_BACKEND_URL : "";
const API = `${BACKEND_URL}/api`;

// Available icons for services
const AVAILABLE_ICONS = [
  { name: 'Wrench', component: Wrench, label: 'Wrench' },
  { name: 'Settings', component: SettingsIcon, label: 'Settings' },
  { name: 'Cog', component: Cog, label: 'Cog/Gear' },
  { name: 'Flame', component: Flame, label: 'Flame/Fire' },
  { name: 'Drill', component: Drill, label: 'Drill' },
  { name: 'Hammer', component: Hammer, label: 'Hammer' },
  { name: 'Zap', component: Zap, label: 'Lightning/Zap' },
  { name: 'Shield', component: Shield, label: 'Shield' },
  { name: 'Network', component: Network, label: 'Network' },
  { name: 'Factory', component: Factory, label: 'Factory' },
  { name: 'Boxes', component: Boxes, label: 'Boxes' },
  { name: 'Truck', component: Truck, label: 'Truck' },
  { name: 'HardHat', component: HardHat, label: 'Hard Hat' },
  { name: 'Cpu', component: Cpu, label: 'Chip/CPU' },
  { name: 'Package', component: Package, label: 'Package' }
];

export const AdminDashboard = () => {
  const { user, logout, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('products');
  
  // Leads states removed

  // Leads loading operations removed

  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [supplies, setSupplies] = useState([]);
  const [contactInfo, setContactInfo] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [siteSettings, setSiteSettings] = useState(null);

  // Shop Employee Uploads & Promotion States
  const [employeeProducts, setEmployeeProducts] = useState([]);
  const [selectedUploads, setSelectedUploads] = useState([]);
  const [promotionsQueue, setPromotionsQueue] = useState([]);
  const [includePromoted, setIncludePromoted] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeViewMode, setEmployeeViewMode] = useState('grid');
  const [employeeSortOrder, setEmployeeSortOrder] = useState('desc');
  const [editingUploadId, setEditingUploadId] = useState(null);
  const [editUploadNameValue, setEditUploadNameValue] = useState('');
  const [activeZoomImage, setActiveZoomImage] = useState(null);

  // Notification states
  const [showNotifications, setShowNotifications] = useState(false);
  const [seenDrafts, setSeenDrafts] = useState(() => {
    try {
      const saved = localStorage.getItem('admin_seen_drafts');
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  });

  // Team Members / Employees Management
  const [teamMembers, setTeamMembers] = useState([]);
  const [newEmployeeName, setNewEmployeeName] = useState('');
  const [addingEmployee, setAddingEmployee] = useState(false);

  // Lightbox Pan and Zoom states
  const [zoomScale, setZoomScale] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Reset zoom states when opening/closing zoom image
  useEffect(() => {
    setZoomScale(1);
    setPanOffset({ x: 0, y: 0 });
    setIsDragging(false);
  }, [activeZoomImage]);

  // Pan and Zoom Interactive event handlers
  const handleMouseDown = (e) => {
    if (zoomScale <= 1) return; // Only allow pan when zoomed in
    e.preventDefault();
    setIsDragging(true);
    setDragStart({ x: e.clientX - panOffset.x, y: e.clientY - panOffset.y });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    setPanOffset({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e) => {
    if (zoomScale <= 1) return;
    const touch = e.touches[0];
    setIsDragging(true);
    setDragStart({ x: touch.clientX - panOffset.x, y: touch.clientY - panOffset.y });
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const touch = e.touches[0];
    setPanOffset({
      x: touch.clientX - dragStart.x,
      y: touch.clientY - dragStart.y
    });
  };

  const [showProductModal, setShowProductModal] = useState(false);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [showSupplyModal, setShowSupplyModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showSiteSettingsModal, setShowSiteSettingsModal] = useState(false);
  
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingService, setEditingService] = useState(null);
  const [editingSupply, setEditingSupply] = useState(null);
  
  const [productForm, setProductForm] = useState({ name: '', description: '', image_url: '', production_time: '' });
  const [serviceForm, setServiceForm] = useState({ name: '', description: '', icon: 'Wrench' });
  const [supplyForm, setSupplyForm] = useState({ name: '', description: '', sort_order: 0 });
  const [contactForm, setContactForm] = useState({
    phone: '', fax: '', email: '', address: '', city: '', province: '', postal_code: '', country: ''
  });
  const [siteSettingsForm, setSiteSettingsForm] = useState({
    logo_url: '', company_name: '', tagline: '', about_title: '', about_content: '', about_image_url: '', hero_image_url: '',
    logo_size: 80, logo_glow_color: '#FF9900', logo_glow_intensity: 0,
    hero_image_opacity: 40, hero_gradient_opacity: 60
  });
  
  // Garbage Collection for Orphaned Image Uploads
  const [pendingUploads, setPendingUploads] = useState([]);
  
  const [deleteDialog, setDeleteDialog] = useState({ 
    isOpen: false, 
    type: null, 
    id: null, 
    name: '' 
  });
  const [isDeleting, setIsDeleting] = useState(false);

  const [employeeActionDialog, setEmployeeActionDialog] = useState({
    isOpen: false,
    type: null,
    username: '',
    displayName: ''
  });
  const [isProcessingEmployee, setIsProcessingEmployee] = useState(false);
  
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingHeroImage, setUploadingHeroImage] = useState(false);
  const [uploadingAboutImage, setUploadingAboutImage] = useState(false);
  const [uploadingProductImage, setUploadingProductImage] = useState(false);

  useEffect(() => {
    // Dynamically insert a noindex meta tag when the admin page is mounted
    // to prevent search engines from indexing the admin panel if they discover the URL
    const meta = document.createElement('meta');
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);

    return () => {
        document.head.removeChild(meta); // Clean up when navigating away
    };
  }, []);

  useEffect(() => {
    if (loading) return; // Wait until AuthContext has finished checking localStorage
    
    if (!isAuthenticated) {
      navigate('/eastend_admin/login');
    } else {
      loadData();
    }
  }, [loading, isAuthenticated, navigate]);

  useEffect(() => {
    if (isAuthenticated && !loading) {
      loadData();
    }
  }, [includePromoted]);

  // Save seenDrafts to localStorage
  useEffect(() => {
    localStorage.setItem('admin_seen_drafts', JSON.stringify(seenDrafts));
  }, [seenDrafts]);

  // Click outside to close notification dropdown
  useEffect(() => {
    if (!showNotifications) return;
    const handleOutsideClick = (e) => {
      if (!e.target.closest('.notifications-container')) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('click', handleOutsideClick);
    return () => document.removeEventListener('click', handleOutsideClick);
  }, [showNotifications]);

  // Prune seenDrafts to avoid infinite growth, keeping only existing draft IDs
  useEffect(() => {
    if (employeeProducts.length > 0) {
      const allDraftIds = new Set(employeeProducts.map(d => d.id));
      setSeenDrafts(prev => {
        const next = prev.filter(id => allDraftIds.has(id));
        if (next.length !== prev.length) {
          return next;
        }
        return prev;
      });
    }
  }, [employeeProducts]);

  const loadData = async () => {
    try {
      const timestamp = Date.now();
      const [productsRes, servicesRes, suppliesRes, contactRes, settingsRes, employeeProductsRes, employeesRes] = await Promise.all([
        axios.get(`${API}/products?t=${timestamp}`),
        axios.get(`${API}/services?t=${timestamp}`),
        axios.get(`${API}/supplies?t=${timestamp}`),
        axios.get(`${API}/contact-info?t=${timestamp}`),
        axios.get(`${API}/site-settings?t=${timestamp}`),
        axios.get(`${API}/employee/products-admin?t=${timestamp}&include_promoted=${includePromoted}`, { withCredentials: true }),
        axios.get(`${API}/admin/employees?t=${timestamp}`, { withCredentials: true }).catch(err => {
          console.error('Failed to load employees:', err);
          return { data: [] };
        })
      ]);
      
      setProducts(Array.isArray(productsRes.data) ? productsRes.data : []);
      setServices(Array.isArray(servicesRes.data) ? servicesRes.data : []);
      setSupplies(Array.isArray(suppliesRes.data) ? suppliesRes.data : []);
      setContactInfo(contactRes.data);
      setSiteSettings(settingsRes.data);
      setEmployeeProducts(Array.isArray(employeeProductsRes.data) ? employeeProductsRes.data : []);
      setTeamMembers(Array.isArray(employeesRes.data) ? employeesRes.data : []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    }
  };

  const cancelPendingUploads = async () => {
    for (const url of pendingUploads) {
      try {
        await axios.post(`${API}/delete-image`, { url }, { withCredentials: true });
      } catch (err) {
        console.error("Failed to delete orphaned image:", url);
      }
    }
    setPendingUploads([]);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const formatEmployeeDisplayName = (username) => {
    if (!username) return '';
    const clean = username.replace('_ei', '').replace(/_/g, ' ');
    return clean.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const handleAddEmployee = async (e) => {
    e.preventDefault();
    const name = newEmployeeName.trim();
    if (!name) return;

    setAddingEmployee(true);
    try {
      const response = await axios.post(`${API}/admin/employees`, { name }, { withCredentials: true });
      if (response.data.success) {
        toast.success(response.data.detail || 'Employee added successfully');
        setNewEmployeeName('');
        await loadData();
      } else {
        toast.error(response.data.detail || 'Failed to add employee');
      }
    } catch (error) {
      console.error('Error adding employee:', error);
      toast.error(error.response?.data?.detail || 'Failed to add employee');
    } finally {
      setAddingEmployee(false);
    }
  };

  const handleDeleteEmployee = (username) => {
    const displayName = formatEmployeeDisplayName(username);
    setEmployeeActionDialog({
      isOpen: true,
      type: 'delete',
      username: username,
      displayName: displayName
    });
  };

  const handleClearEmployeePassword = (username) => {
    const displayName = formatEmployeeDisplayName(username);
    setEmployeeActionDialog({
      isOpen: true,
      type: 'clear_password',
      username: username,
      displayName: displayName
    });
  };

  const handleConfirmEmployeeAction = async () => {
    const { type, username, displayName } = employeeActionDialog;
    if (!username || !type) return;

    setIsProcessingEmployee(true);
    try {
      if (type === 'clear_password') {
        const response = await axios.put(`${API}/admin/employees`, { username }, { withCredentials: true });
        if (response.data.success) {
          toast.success(response.data.detail || 'Password cleared successfully');
          await loadData();
        } else {
          toast.error(response.data.detail || 'Failed to clear password');
        }
      } else if (type === 'delete') {
        const response = await axios.delete(`${API}/admin/employees?username=${encodeURIComponent(username)}`, { withCredentials: true });
        if (response.data.success) {
          toast.success(response.data.detail || 'Employee deleted successfully');
          if (selectedEmployee === username.replace('_ei', '')) {
            setSelectedEmployee(null);
          }
          await loadData();
        } else {
          toast.error(response.data.detail || 'Failed to delete employee');
        }
      }
      setEmployeeActionDialog({ isOpen: false, type: null, username: '', displayName: '' });
    } catch (error) {
      console.error(`Error performing employee ${type}:`, error);
      toast.error(error.response?.data?.detail || `Failed to perform employee ${type}`);
    } finally {
      setIsProcessingEmployee(false);
    }
  };

  // Helper function to capitalize employee names
  const capitalizeFirstLetter = (string) => {
    if (!string) return '';
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  // Handle employee product selections
  const handleToggleSelectUpload = (id) => {
    if (selectedUploads.includes(id)) {
      setSelectedUploads(selectedUploads.filter(x => x !== id));
    } else {
      setSelectedUploads([...selectedUploads, id]);
    }
  };

  // Launch promotion flow for selected uploads as a single product with multiple images
  const handlePromoteSelectedUploads = () => {
    const queue = employeeProducts.filter(x => selectedUploads.includes(x.id));
    setPromotionsQueue(queue);
    
    // Group all selected image URLs into a comma-separated list
    const imageUrls = queue.map(draft => draft.image_url).filter(Boolean).join(',');
    // Pre-populate product name with the first draft that has a product name
    const initialName = queue.find(draft => draft.product_name)?.product_name || '';
    
    // Calculate the maximum production time among selected drafts
    const productionTimes = queue
      .map(draft => {
        const val = draft.production_time !== undefined ? draft.production_time : draft.productionTime;
        return val;
      })
      .filter(t => t !== null && t !== undefined && t !== '')
      .map(t => {
        const parsed = parseInt(t, 10);
        return isNaN(parsed) ? null : parsed;
      })
      .filter(t => t !== null);
    const maxProductionTime = productionTimes.length > 0 ? Math.max(...productionTimes).toString() : '';

    setProductForm({
      name: initialName,
      description: '',
      image_url: imageUrls,
      production_time: maxProductionTime
    });
    setEditingProduct(null); // Ensure it triggers creation logic, not update logic
    setShowProductModal(true);
  };

  // Safely close the product creation modal and clean up promotion queue
  const handleCloseProductModal = () => {
    setShowProductModal(false);
    setPromotionsQueue([]);
    setEditingProduct(null);
    setProductForm({ name: '', description: '', image_url: '', production_time: '' });
  };

  // Delete employee upload directly
  const handleDeleteEmployeeUpload = async (upload) => {
    if (!window.confirm(`Are you sure you want to delete this upload by "${upload.uploaded_by}"? This will permanently remove it from storage.`)) {
      return;
    }
    try {
      await axios.delete(`${API}/employee/products-admin?id=${upload.id}`, { withCredentials: true });
      toast.success("Employee upload deleted successfully");
      loadData();
    } catch (err) {
      console.error("Delete draft failed", err);
      toast.error("Failed to delete employee upload");
    }
  };

  // Bulk delete selected employee uploads
  const handleDeleteSelectedUploads = async () => {
    if (selectedUploads.length === 0) return;
    
    if (!window.confirm(`Are you sure you want to delete the ${selectedUploads.length} selected employee upload(s)? This will permanently remove them from storage.`)) {
      return;
    }

    try {
      const idsStr = selectedUploads.join(',');
      await axios.delete(`${API}/employee/products-admin?id=${idsStr}`, { withCredentials: true });
      toast.success(`Deleted ${selectedUploads.length} employee upload(s) successfully`);
      setSelectedUploads([]); // Clear selection
      loadData();
    } catch (err) {
      console.error("Bulk delete drafts failed", err);
      toast.error("Failed to delete selected employee upload(s)");
    }
  };

  // Update employee upload product name directly from admin dashboard
  const handleSaveUploadName = async (id) => {
    if (!editUploadNameValue.trim()) {
      toast.error("Product name cannot be empty");
      return;
    }
    try {
      await axios.put(`${API}/employee/products-admin?id=${id}`, {
        product_name: editUploadNameValue
      }, { withCredentials: true });
      
      toast.success("Product name updated successfully!");
      setEditingUploadId(null);
      loadData();
    } catch (err) {
      console.error("Update draft name failed", err);
      toast.error("Failed to update product name");
    }
  };

  const handleCreateProduct = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/products`, productForm, {
        withCredentials: true
      });
      const createdProduct = response.data;
      toast.success(`Product "${productForm.name}" created successfully`);
      
      // If we are promoting from the employee uploads queue
      if (promotionsQueue.length > 0) {
        // Link all promoted drafts to the new product (setting product_id)
        for (const draft of promotionsQueue) {
          try {
            await axios.put(`${API}/employee/products-admin?id=${draft.id}`, {
              product_id: createdProduct.id
            }, { withCredentials: true });
          } catch (err) {
            console.error("Failed to link promoted draft", err);
          }
        }
        
        setShowProductModal(false);
        setPromotionsQueue([]);
        setSelectedUploads([]);
        setProductForm({ name: '', description: '', image_url: '', production_time: '' });
        toast.success('Successfully promoted all selected employee uploads!');
        loadData();
      } else {
        // Standard single manual creation
        setShowProductModal(false);
        setPendingUploads([]);
        setProductForm({ name: '', description: '', image_url: '', production_time: '' });
        loadData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create product');
    }
  };

  const handleUpdateProduct = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/products/${editingProduct.id}`, productForm, {
        withCredentials: true
      });
      toast.success('Product updated successfully');
      setShowProductModal(false);
      setPendingUploads([]);
      setEditingProduct(null);
      setProductForm({ name: '', description: '', image_url: '', production_time: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update product');
    }
  };

  const handleDeleteProduct = (product) => {
    setDeleteDialog({
      isOpen: true,
      type: 'product',
      id: product.id,
      name: product.name
    });
  };

  const handleConfirmDelete = async () => {
    const { type, id } = deleteDialog;
    if (!id || !type) return;

    setIsDeleting(true);
    try {
      const endpoint = type === 'product' ? 'products' : type === 'service' ? 'services' : 'supplies';
      await axios.delete(`${API}/${endpoint}/${id}`, {
        withCredentials: true
      });
      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} deleted successfully`);
      setDeleteDialog({ isOpen: false, type: null, id: null, name: '' });
      loadData();
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error(error.response?.data?.detail || `Failed to delete ${type}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCreateService = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/services`, serviceForm, {
        withCredentials: true
      });
      toast.success('Service created successfully');
      setShowServiceModal(false);
      setPendingUploads([]);
      setServiceForm({ name: '', description: '', icon: 'Wrench' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create service');
    }
  };

  const handleUpdateService = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/services/${editingService.id}`, serviceForm, {
        withCredentials: true
      });
      toast.success('Service updated successfully');
      setShowServiceModal(false);
      setPendingUploads([]);
      setEditingService(null);
      setServiceForm({ name: '', description: '', icon: 'Wrench' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update service');
    }
  };

  const handleDeleteService = (service) => {
    setDeleteDialog({
      isOpen: true,
      type: 'service',
      id: service.id,
      name: service.name
    });
  };

  const handleCreateSupply = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/supplies`, supplyForm, { withCredentials: true });
      toast.success('Supply created successfully');
      setShowSupplyModal(false);
      setPendingUploads([]);
      setSupplyForm({ name: '', description: '', sort_order: 0 });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create supply');
    }
  };

  const handleUpdateSupply = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/supplies/${editingSupply.id}`, supplyForm, { withCredentials: true });
      toast.success('Supply updated successfully');
      setShowSupplyModal(false);
      setPendingUploads([]);
      setEditingSupply(null);
      setSupplyForm({ name: '', description: '', sort_order: 0 });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update supply');
    }
  };

  const handleDeleteSupply = (supply) => {
    setDeleteDialog({
      isOpen: true,
      type: 'supply',
      id: supply.id,
      name: supply.name
    });
  };

  const handleUpdateContactInfo = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/contact-info`, contactForm, {
        withCredentials: true
      });
      toast.success('Contact info updated successfully');
      setShowContactModal(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update contact info');
    }
  };

  const openEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      description: product.description,
      image_url: product.image_url,
      production_time: product.production_time !== null && product.production_time !== undefined ? product.production_time.toString() : ''
    });
    setShowProductModal(true);
  };

  const openEditService = (service) => {
    setEditingService(service);
    setServiceForm({
      name: service.name,
      description: service.description,
      icon: service.icon
    });
    setShowServiceModal(true);
  };

  const openEditContactInfo = () => {
    if (contactInfo) {
      setContactForm(contactInfo);
      setShowContactModal(true);
    }
  };

  const openEditSiteSettings = () => {
    if (siteSettings) {
      setSiteSettingsForm(siteSettings);
      setShowSiteSettingsModal(true);
    }
  };

  const handleUpdateSiteSettings = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/site-settings`, siteSettingsForm, {
        withCredentials: true
      });
      toast.success('Site settings updated successfully');
      setShowSiteSettingsModal(false);
      setPendingUploads([]);
      loadData();
    } catch (error) {
      console.error('Error updating settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to update settings');
    }
  };

  const handleLogoUpload = async (file) => {
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only image files (JPEG, PNG, WebP, GIF) are allowed');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setUploadingLogo(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/upload-logo`, formData, {
        withCredentials: true,
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setSiteSettingsForm({ ...siteSettingsForm, logo_url: response.data.logo_url });
      setPendingUploads(prev => [...prev, response.data.logo_url]);
      toast.success('Logo uploaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleImageUpload = async (input, imageType, updateField) => {
    if (!input) return;
    const files = Array.isArray(input) ? input : [input];
    if (files.length === 0) return;

    if (imageType === 'hero') setUploadingHeroImage(true);
    else if (imageType === 'about') setUploadingAboutImage(true);
    else if (imageType === 'product') setUploadingProductImage(true);

    let successCount = 0;
    let urls = [];

    try {
      for (const file of files) {
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
        if (!allowedTypes.includes(file.type)) {
          toast.error(`"${file.name}" is not an allowed image format`);
          continue;
        }

        if (file.size > 10 * 1024 * 1024) {
          toast.error(`"${file.name}" must be less than 10MB`);
          continue;
        }

        const formData = new FormData();
        formData.append('file', file);
        formData.append('image_type', imageType);

        const response = await axios.post(`${API}/upload-image`, formData, {
          withCredentials: true,
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        urls.push(response.data.image_url);
        setPendingUploads(prev => [...prev, response.data.image_url]);
        successCount++;
      }

      if (successCount > 0) {
        if (imageType === 'product') {
          updateField(urls);
        } else {
          updateField(urls[0]);
        }
        toast.success(successCount > 1 ? `Uploaded ${successCount} images successfully` : 'Image uploaded successfully');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image(s)');
    } finally {
      if (imageType === 'hero') setUploadingHeroImage(false);
      else if (imageType === 'about') setUploadingAboutImage(false);
      else if (imageType === 'product') setUploadingProductImage(false);
    }
  };

  // Group employee products by uploader
  const employeeGroups = employeeProducts.reduce((groups, item) => {
    const uploader = (item.uploaded_by || 'unknown').replace('_ei', '');
    if (!groups[uploader]) groups[uploader] = [];
    groups[uploader].push(item);
    return groups;
  }, {});

  const employeesList = Array.from(new Set([
    ...teamMembers.map(m => m.username.replace('_ei', '')),
    ...Object.keys(employeeGroups)
  ])).sort();
  const currentEmployee = selectedEmployee && employeesList.includes(selectedEmployee)
    ? selectedEmployee
    : (employeesList[0] || null);

  const displayedItems = currentEmployee ? (employeeGroups[currentEmployee] || []) : [];

  // Helper to mark employee drafts as seen
  const markEmployeeDraftsAsSeen = (uploader) => {
    const drafts = employeeGroups[uploader] || [];
    const draftIds = drafts.map(d => d.id);
    setSeenDrafts(prev => {
      const next = [...prev];
      draftIds.forEach(id => {
        if (!next.includes(id)) {
          next.push(id);
        }
      });
      return next;
    });
  };

  // Automatically mark drafts of the currently viewed employee as read
  useEffect(() => {
    if (activeTab === 'employeeProducts' && currentEmployee && displayedItems.length > 0) {
      const draftIds = displayedItems.map(d => d.id);
      const unseenIds = draftIds.filter(id => !seenDrafts.includes(id));
      if (unseenIds.length > 0) {
        setSeenDrafts(prev => {
          const next = [...prev];
          unseenIds.forEach(id => {
            if (!next.includes(id)) {
              next.push(id);
            }
          });
          return next;
        });
      }
    }
  }, [activeTab, currentEmployee, displayedItems, seenDrafts]);

  // Derived notification calculations
  const unreadDrafts = employeeProducts.filter(draft => {
    const isDraft = !draft.product_id;
    const isUnseen = !seenDrafts.includes(draft.id);
    return isDraft && isUnseen;
  });

  const unreadByEmployee = unreadDrafts.reduce((acc, draft) => {
    const uploader = (draft.uploaded_by || 'unknown').replace('_ei', '');
    if (!acc[uploader]) {
      acc[uploader] = [];
    }
    acc[uploader].push(draft);
    return acc;
  }, {});

  const notificationCount = unreadDrafts.length;

  // Sort displayed items
  const sortedItems = [...displayedItems].sort((a, b) => {
    const dateA = new Date(a.created_at);
    const dateB = new Date(b.created_at);
    return employeeSortOrder === 'asc' ? dateA - dateB : dateB - dateA;
  });

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Toaster position="top-right" theme="dark" />
      
      <div className="bg-[#121212] border-b border-[#27272A] px-4 py-4 sm:py-5">
        <div className="max-w-[95%] xl:max-w-[92%] 2xl:max-w-[90%] mx-auto flex items-center justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-xl sm:text-3xl font-bold text-[#FF9900] uppercase leading-tight tracking-wide">Admin Dashboard</h1>
            <p className="text-sm sm:text-base text-gray-400 mt-1">Welcome, <span className="text-white font-bold">{user?.username}</span></p>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            {/* Bell Notification */}
            <div className="relative notifications-container">
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative flex items-center justify-center p-2.5 bg-[#1E1E1E] border border-[#27272A] text-[#F3F4F6] hover:bg-[#27272A] hover:text-[#FF9900] transition-all duration-300 rounded-sm h-[42px] w-[42px]"
                title="Notifications"
              >
                <Bell className="w-4.5 h-4.5" />
                {notificationCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 bg-red-600 text-white text-[10px] font-bold h-5 w-5 rounded-full flex items-center justify-center border border-[#121212] leading-none">
                    {notificationCount}
                  </span>
                )}
              </button>
              
              {/* Dropdown Menu */}
              <AnimatePresence>
                {showNotifications && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="absolute right-0 mt-2 w-80 bg-[#121212] border border-[#27272A] rounded-sm shadow-2xl z-50 overflow-hidden"
                  >
                    <div className="p-3 border-b border-[#27272A] bg-[#171717] flex items-center justify-between">
                      <span className="text-xs font-extrabold uppercase tracking-wider text-[#FF9900]">Notifications</span>
                      {notificationCount > 0 && (
                        <button
                          onClick={() => {
                            const allIds = unreadDrafts.map(d => d.id);
                            setSeenDrafts(prev => [...new Set([...prev, ...allIds])]);
                          }}
                          className="text-[10px] font-bold text-gray-500 hover:text-white uppercase transition-colors"
                        >
                          Mark all read
                        </button>
                      )}
                    </div>
                    
                    <div className="max-h-64 overflow-y-auto divide-y divide-[#1C1C1E]">
                      {Object.keys(unreadByEmployee).length === 0 ? (
                        <div className="p-8 text-center text-xs text-gray-600 font-bold uppercase tracking-wider">
                          No new notifications
                        </div>
                      ) : (
                        Object.keys(unreadByEmployee).map(uploader => {
                          const count = unreadByEmployee[uploader].length;
                          const displayName = uploader.charAt(0).toUpperCase() + uploader.slice(1);
                          return (
                            <button
                              key={uploader}
                              onClick={() => {
                                setActiveTab('employeeProducts');
                                setSelectedEmployee(uploader);
                                markEmployeeDraftsAsSeen(uploader);
                                setShowNotifications(false);
                              }}
                              className="w-full text-left p-3.5 hover:bg-[#161616] transition-colors flex gap-3 items-start"
                            >
                              <div className="bg-[#FF9900]/10 border border-[#FF9900]/20 p-2 rounded-full text-[#FF9900] flex-shrink-0">
                                <HardHat className="w-4 h-4" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="text-xs text-gray-200 leading-normal">
                                  <span className="font-bold text-white">{displayName}</span> uploaded <span className="font-bold text-[#FF9900]">{count}</span> new product draft{count > 1 ? 's' : ''}.
                                </p>
                                <span className="text-[9px] font-bold uppercase tracking-wider text-gray-600 mt-1 block">
                                  Click to review
                                </span>
                              </div>
                            </button>
                          );
                        })
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <Link
              to="/"
              data-testid="homepage-button"
              className="flex items-center gap-2.5 bg-[#1E1E1E] border border-[#27272A] text-[#F3F4F6] px-4 py-2.5 font-bold uppercase text-base sm:text-sm hover:bg-[#27272A] hover:text-[#FF9900] transition-all duration-300"
            >
              <Home className="w-4.5 h-4.5" />
              <span className="hidden sm:inline">Homepage</span>
            </Link>
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              className="flex items-center gap-2.5 bg-[#FF9900] text-black px-4 py-2.5 font-bold uppercase text-base sm:text-sm hover:bg-white transition-all duration-300"
            >
              <LogOut className="w-4.5 h-4.5" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
        
        <AlertDialog open={deleteDialog.isOpen} onOpenChange={(open) => !open && setDeleteDialog({ ...deleteDialog, isOpen: false })}>
          <AlertDialogContent className="bg-[#121212] border-[#27272A] text-white">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-[#FF9900] uppercase font-bold text-2xl">Confirm Deletion</AlertDialogTitle>
              <AlertDialogDescription className="text-gray-400">
                Are you sure you want to delete <span className="text-white font-bold">"{deleteDialog.name}"</span>? 
                This action cannot be undone and will permanently remove this {deleteDialog.type} from the database.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="mt-6">
              <AlertDialogCancel className="bg-transparent border-[#27272A] text-gray-400 hover:bg-[#1A1A1A] hover:text-white uppercase font-bold">Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleConfirmDelete}
                disabled={isDeleting}
                className="bg-red-600 text-white hover:bg-red-700 uppercase font-bold px-8"
              >
                {isDeleting ? 'Deleting...' : 'Delete Permanently'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog 
          open={employeeActionDialog.isOpen} 
          onOpenChange={(open) => !open && setEmployeeActionDialog({ ...employeeActionDialog, isOpen: false })}
        >
          <AlertDialogContent className="bg-[#121212] border-[#27272A] text-white">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-[#FF9900] uppercase font-bold text-2xl">
                {employeeActionDialog.type === 'clear_password' ? 'Reset Employee Password' : 'Delete Employee Account'}
              </AlertDialogTitle>
              <AlertDialogDescription className="text-gray-400">
                {employeeActionDialog.type === 'clear_password' ? (
                  <>
                    Are you sure you want to clear the password for employee <span className="text-white font-bold">"{employeeActionDialog.displayName}"</span>?<br/><br/>
                    This will reset their password, and they will be prompted to set a new one the next time they try to log in.
                  </>
                ) : (
                  <>
                    Are you sure you want to delete employee account <span className="text-white font-bold">"{employeeActionDialog.displayName}"</span>?<br/><br/>
                    This will remove their ability to log in, but will not delete their uploaded products.
                  </>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="mt-6">
              <AlertDialogCancel className="bg-transparent border-[#27272A] text-gray-400 hover:bg-[#1A1A1A] hover:text-white uppercase font-bold">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleConfirmEmployeeAction}
                disabled={isProcessingEmployee}
                className={`${
                  employeeActionDialog.type === 'clear_password' 
                    ? 'bg-[#FF9900] text-black hover:bg-white' 
                    : 'bg-red-600 hover:bg-red-700 text-white'
                } font-bold uppercase disabled:opacity-50`}
              >
                {isProcessingEmployee 
                  ? 'Processing...' 
                  : (employeeActionDialog.type === 'clear_password' ? 'Clear Password' : 'Delete Account')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      <div className="max-w-[95%] xl:max-w-[92%] 2xl:max-w-[90%] mx-auto px-4 sm:px-6 py-8 sm:py-10">
        <div className="overflow-x-auto custom-scrollbar -mx-4 sm:-mx-6 px-4 sm:px-6 mb-10 border-b border-[#27272A]">
          <div className="flex gap-2 sm:gap-6 min-w-max">
            {[
              { id: 'products', label: 'Products', icon: Box },
              { id: 'services', label: 'Services', icon: Package },
              { id: 'supplies', label: 'Supplies', icon: Boxes },
              { id: 'employeeProducts', label: 'Employee Products', icon: HardHat },
              { id: 'site', label: 'Site Settings', icon: Settings },
              { id: 'contact', label: 'Contact Info', icon: Phone }
            ].map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  data-testid={`tab-${tab.id}`}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 sm:gap-3 px-4 sm:px-8 py-4 font-bold uppercase text-sm sm:text-base transition-colors border-b-4 whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-[#FF9900] text-[#FF9900]'
                      : 'border-transparent text-gray-400 hover:text-[#FF9900]'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {activeTab === 'products' && (
          <div>
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
              <h2 className="text-3xl sm:text-4xl font-bold text-[#F3F4F6] uppercase">Manage Products</h2>
              <button
                onClick={() => {
                  setEditingProduct(null);
                  setProductForm({ name: '', description: '', image_url: '', production_time: '' });
                  setShowProductModal(true);
                }}
                data-testid="add-product-button"
                className="flex items-center gap-2.5 bg-[#FF9900] text-black px-5 py-3 sm:px-8 sm:py-4 font-bold uppercase text-sm sm:text-base hover:bg-white transition-colors"
              >
                <Plus className="w-5 h-5" />
                Add Product
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {products.map(product => {
                const imageUrls = product.image_url ? product.image_url.split(',').map(x => x.trim()).filter(Boolean) : [];
                const firstImg = imageUrls[0] || '/images/placeholder.jpg';
                return (
                  <motion.div
                    key={product.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="bg-[#121212] border border-[#27272A] overflow-hidden relative"
                  >
                    <div className="relative h-72 overflow-hidden bg-[#0A0A0A]">
                      <img src={firstImg} alt={product.name} className="w-full h-full object-cover" />
                      {imageUrls.length > 1 && (
                        <span className="absolute top-3 right-3 bg-black/85 text-[#FF9900] text-[10px] font-bold px-3 py-1 border border-[#FF9900]/30 rounded-full tracking-wider uppercase">
                          {imageUrls.length} Photos
                        </span>
                      )}
                    </div>
                    <div className="p-6">
                      <h3 className="text-xl sm:text-2xl font-bold text-[#F3F4F6] mb-3 uppercase tracking-tight">{product.name}</h3>
                      <p className="text-sm sm:text-base text-gray-300 mb-5 leading-relaxed">{product.description}</p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => openEditProduct(product)}
                          data-testid={`edit-product-${product.id}`}
                          className="flex items-center gap-2 bg-[#1E1E1E] text-[#FF9900] px-4 py-2 text-sm font-bold hover:bg-[#27272A] transition-colors"
                        >
                          <Edit2 className="w-4 h-4" />
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteProduct(product)}
                          data-testid={`delete-product-${product.id}`}
                          className="flex items-center gap-2 bg-red-900/20 text-red-400 px-4 py-2 text-sm font-bold hover:bg-red-900/40 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete
                        </button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        )}

        {activeTab === 'services' && (
          <div>
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
              <h2 className="text-3xl sm:text-4xl font-bold text-[#F3F4F6] uppercase">Manage Services</h2>
              <button
                onClick={() => {
                  setEditingService(null);
                  setServiceForm({ name: '', description: '', icon: 'Wrench' });
                  setShowServiceModal(true);
                }}
                data-testid="add-service-button"
                className="flex items-center gap-2.5 bg-[#FF9900] text-black px-5 py-2.5 sm:px-8 sm:py-4 font-bold uppercase text-sm sm:text-base hover:bg-white transition-colors"
              >
                <Plus className="w-5 h-5" />
                Add Service
              </button>
            </div>

            <div className="space-y-6">
              {services.map(service => (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="bg-[#121212] border border-[#27272A] p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center justify-between gap-6"
                >
                  <div className="flex items-center gap-6">
                    {(() => {
                      const iconEntry = AVAILABLE_ICONS.find(i => i.name === service.icon);
                      const IconComponent = iconEntry ? iconEntry.component : Wrench;
                      return (
                        <div className="flex-shrink-0 w-16 h-16 bg-[#FF9900]/10 border border-[#FF9900]/30 flex items-center justify-center">
                          <IconComponent className="w-8 h-8 text-[#FF9900]" />
                        </div>
                      );
                    })()}
                    <div>
                      <h3 className="text-2xl font-bold text-[#F3F4F6] mb-2 uppercase tracking-wide">{service.name}</h3>
                      <p className="text-sm sm:text-base text-gray-300 leading-relaxed max-w-4xl">{service.description}</p>
                    </div>
                  </div>
                  <div className="flex gap-2 sm:flex-shrink-0">
                    <button
                      onClick={() => openEditService(service)}
                      data-testid={`edit-service-${service.id}`}
                      className="flex items-center gap-2 bg-[#1E1E1E] text-[#FF9900] px-4 py-2 text-sm font-bold hover:bg-[#27272A] transition-colors"
                    >
                      <Edit2 className="w-4 h-4" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteService(service)}
                      data-testid={`delete-service-${service.id}`}
                      className="flex items-center gap-2 bg-red-900/20 text-red-400 px-4 py-2 text-sm font-bold hover:bg-red-900/40 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'supplies' && (
          <div>
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
              <h2 className="text-3xl sm:text-4xl font-bold text-[#F3F4F6] uppercase">Manage Supplies</h2>
              <button
                onClick={() => {
                  setEditingSupply(null);
                  setSupplyForm({ name: '', description: '', sort_order: supplies.length });
                  setShowSupplyModal(true);
                }}
                data-testid="add-supply-button"
                className="flex items-center gap-2.5 bg-[#FF9900] text-black px-5 py-2.5 sm:px-8 sm:py-4 font-bold uppercase text-sm sm:text-base hover:bg-white transition-colors"
              >
                <Plus className="w-5 h-5" />
                Add Supply
              </button>
            </div>

            <div className="space-y-6">
              {supplies.map(supply => (
                <motion.div
                  key={supply.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="bg-[#121212] border border-[#27272A] p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center justify-between gap-6"
                >
                  <div>
                    <h3 className="text-2xl font-bold text-[#F3F4F6] mb-2 uppercase tracking-wide">{supply.name}</h3>
                    <p className="text-sm sm:text-base text-gray-300 leading-relaxed max-w-4xl">{supply.description}</p>
                  </div>
                  <div className="flex gap-2 sm:flex-shrink-0">
                    <button
                      onClick={() => {
                        setEditingSupply(supply);
                        setSupplyForm({ name: supply.name, description: supply.description, sort_order: supply.sort_order });
                        setShowSupplyModal(true);
                      }}
                      data-testid={`edit-supply-${supply.id}`}
                      className="flex items-center gap-2 bg-[#1E1E1E] text-[#FF9900] px-4 py-2 text-sm font-bold hover:bg-[#27272A] transition-colors"
                    >
                      <Edit2 className="w-4 h-4" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteSupply(supply)}
                      data-testid={`delete-supply-${supply.id}`}
                      className="flex items-center gap-2 bg-red-900/20 text-red-400 px-4 py-2 text-sm font-bold hover:bg-red-900/40 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'site' && (
          <div>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
              <h2 className="text-2xl sm:text-3xl font-bold text-[#F3F4F6] uppercase">Site Settings</h2>
              <button
                onClick={openEditSiteSettings}
                data-testid="edit-site-settings-button"
                className="flex items-center gap-2 bg-[#FF9900] text-black px-4 py-2 sm:px-6 sm:py-3 font-bold uppercase text-sm hover:bg-white transition-colors"
              >
                <Edit2 className="w-5 h-5" />
                Edit Settings
              </button>
            </div>

            {siteSettings && (
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-[#121212] border border-[#27272A] p-8">
                  <h3 className="text-xl font-bold text-[#FF9900] mb-6 uppercase">Branding</h3>
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-gray-500 mb-1">LOGO URL</p>
                      {siteSettings.logo_url ? (
                        <div className="flex items-center gap-4">
                          <img src={siteSettings.logo_url} alt="Logo" className="w-16 h-16 object-contain bg-white/10 p-2" />
                          <p className="text-sm text-[#F3F4F6] break-all">{siteSettings.logo_url}</p>
                        </div>
                      ) : (
                        <p className="text-gray-400 italic">No logo set (using default icon)</p>
                      )}
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 mb-1">COMPANY NAME</p>
                      <p className="text-lg text-[#F3F4F6]">{siteSettings.company_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 mb-1">TAGLINE</p>
                      <p className="text-lg text-[#F3F4F6]">{siteSettings.tagline}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-[#121212] border border-[#27272A] p-8">
                  <h3 className="text-xl font-bold text-[#FF9900] mb-6 uppercase">Images</h3>
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-gray-500 mb-2">HERO IMAGE</p>
                      <img src={siteSettings.hero_image_url} alt="Hero" className="w-full h-32 object-cover border-2 border-[#27272A]" />
                      <p className="text-base text-gray-400 mt-1">{siteSettings.hero_image_url}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 mb-2">ABOUT IMAGE</p>
                      <img src={siteSettings.about_image_url} alt="About" className="w-full h-32 object-cover border-2 border-[#27272A]" />
                      <p className="text-base text-gray-400 mt-1">{siteSettings.about_image_url}</p>
                    </div>
                  </div>
                </div>

                <div className="md:col-span-2 bg-[#121212] border border-[#27272A] p-8">
                  <h3 className="text-xl font-bold text-[#FF9900] mb-4 uppercase">{siteSettings.about_title}</h3>
                  <p className="text-gray-300 leading-relaxed whitespace-pre-line">{siteSettings.about_content}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'contact' && (
          <div>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
              <h2 className="text-2xl sm:text-3xl font-bold text-[#F3F4F6] uppercase">Contact Information</h2>
              <button
                onClick={openEditContactInfo}
                data-testid="edit-contact-button"
                className="flex items-center gap-2 bg-[#FF9900] text-black px-4 py-2 sm:px-6 sm:py-3 font-bold uppercase text-sm hover:bg-white transition-colors"
              >
                <Edit2 className="w-5 h-5" />
                Edit Info
              </button>
            </div>

            {contactInfo && (
              <div className="bg-[#121212] border border-[#27272A] p-8">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">PHONE</p>
                    <p className="text-lg text-[#F3F4F6]">{contactInfo.phone}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">FAX</p>
                    <p className="text-lg text-[#F3F4F6]">{contactInfo.fax}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">EMAIL</p>
                    <p className="text-lg text-[#F3F4F6]">{contactInfo.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">ADDRESS</p>
                    <p className="text-lg text-[#F3F4F6]">
                      {contactInfo.address}<br />
                      {contactInfo.city}, {contactInfo.province} {contactInfo.postal_code}<br />
                      {contactInfo.country}
                    </p>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-[#27272A]">
                  <p className="text-sm text-gray-500 mb-1">BUSINESS HOURS</p>
                  <p className="text-[#F3F4F6] whitespace-pre-line">
                    {contactInfo.business_hours || <span className="text-gray-600 italic">Not set — click Edit Info to add business hours</span>}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'employeeProducts' && (
          <div>
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
              <div>
                <h2 className="text-3xl sm:text-4xl font-bold text-[#F3F4F6] uppercase">Employee Product Uploads</h2>
                <p className="text-sm sm:text-base text-gray-400 mt-2">Select photos uploaded from the shop floor to promote them directly to public products.</p>
                
                {/* Include Promoted Checkbox */}
                <div className="flex items-center gap-2.5 mt-3.5 bg-[#1A1A1A] border border-[#27272A] px-3.5 py-2 w-fit rounded-sm">
                  <label className="flex items-center gap-2.5 cursor-pointer text-base sm:text-sm font-bold text-gray-400 hover:text-white uppercase tracking-wider transition-colors">
                    <input
                      type="checkbox"
                      checked={includePromoted}
                      onChange={(e) => setIncludePromoted(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-4.5 h-4.5 border border-white/60 bg-[#121212] hover:border-[#FF9900] peer-checked:border-[#FF9900] peer-checked:bg-[#FF9900] flex items-center justify-center transition-all duration-300 rounded-sm">
                      <Check className="w-3.5 h-3.5 text-black font-black opacity-0 peer-checked:opacity-100 transition-opacity" />
                    </div>
                    <span>Include Promoted Images</span>
                  </label>
                </div>
              </div>
              <button
                onClick={handlePromoteSelectedUploads}
                disabled={selectedUploads.length === 0}
                className="flex items-center gap-2.5 bg-[#FF9900] text-black px-8 py-4 font-bold uppercase text-sm sm:text-base hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus className="w-5 h-5" />
                Add Product {selectedUploads.length > 0 && `(${selectedUploads.length})`}
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 items-start">
              {/* Left Column (~25%): Employees Sidebar */}
              <div className="lg:col-span-1 bg-[#121212] border border-[#27272A] p-5 rounded-sm">
                <h3 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-5 flex items-center gap-2 border-b border-[#27272A] pb-4">
                  <HardHat className="w-4.5 h-4.5 text-[#FF9900]" />
                  Shop Floor Team
                </h3>
                
                {employeesList.length === 0 ? (
                  <p className="text-base text-gray-500 italic py-4 text-center">No team members registered yet.</p>
                ) : (
                  <div className="flex flex-row lg:flex-col overflow-x-auto custom-scrollbar lg:overflow-x-visible gap-2 pb-2 lg:pb-0">
                    {employeesList.map(employee => {
                      const isActive = employee === currentEmployee;
                      const count = employeeGroups[employee]?.length || 0;
                      const memberInfo = teamMembers.find(m => m.username.replace('_ei', '') === employee);
                      const isInitialized = memberInfo ? Boolean(parseInt(memberInfo.is_initialized)) : true;
                      
                      return (
                        <div
                          key={employee}
                          onClick={() => setSelectedEmployee(employee)}
                          className={`flex items-center justify-between gap-3 px-4 py-3 text-sm font-bold uppercase tracking-wider rounded-sm transition-all duration-300 w-full text-left cursor-pointer border-l-2 relative group ${
                            isActive
                              ? 'bg-[#1E1E1E] text-white border-l-[#FF9900] shadow-sm'
                              : 'text-gray-400 hover:text-white hover:bg-[#151515] border-l-transparent'
                          }`}
                        >
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            {memberInfo && (
                              <span
                                className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                                  isInitialized ? 'bg-green-500' : 'bg-amber-500 animate-pulse'
                                }`}
                                title={isInitialized ? 'Password initialized' : 'Password setup pending'}
                              />
                            )}
                            <span className="truncate" title={employee}>{formatEmployeeDisplayName(employee)}</span>
                          </div>
                          
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            <span className={`text-base font-extrabold px-2.5 py-0.5 rounded-full border ${
                              isActive
                                ? 'bg-[#FF9900] text-black border-[#FF9900]'
                                : 'bg-[#1C1C1E] text-gray-400 border-[#27272A]'
                            }`}>
                              {count}
                            </span>
                            
                            {memberInfo && (
                              <>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleClearEmployeePassword(memberInfo.username);
                                  }}
                                  className="text-gray-500 hover:text-[#FF9900] p-0.5 rounded transition-all opacity-0 group-hover:opacity-100"
                                  title={`Clear password for ${formatEmployeeDisplayName(memberInfo.username)}`}
                                >
                                  <Key className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteEmployee(memberInfo.username);
                                  }}
                                  className="text-gray-500 hover:text-red-500 p-0.5 rounded transition-all opacity-0 group-hover:opacity-100"
                                  title={`Delete ${memberInfo.username}`}
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Add Team Member Form */}
                <form onSubmit={handleAddEmployee} className="mt-6 pt-6 border-t border-[#27272A] space-y-3">
                  <h4 className="text-base font-bold text-gray-500 uppercase tracking-widest">Add Team Member</h4>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Enter name (e.g. John Doe)"
                      value={newEmployeeName}
                      onChange={(e) => setNewEmployeeName(e.target.value)}
                      disabled={addingEmployee}
                      className="w-full bg-[#1A1A1A] border border-[#27272A] text-sm px-4 py-3 text-white focus:outline-none focus:border-[#FF9900] disabled:opacity-50 uppercase rounded-sm"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={addingEmployee || !newEmployeeName.trim()}
                    className="w-full flex items-center justify-center gap-2 bg-[#FF9900] text-black px-5 py-3 font-bold uppercase text-sm hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed rounded-sm"
                  >
                    {addingEmployee ? 'Adding...' : 'Add Member'}
                  </button>
                  <p className="text-base text-gray-500 normal-case leading-relaxed mt-1">
                    System automatically appends <strong>_ei</strong> and formats lowercase.
                  </p>
                </form>
              </div>

              {/* Right Column (~75%): Selected Employee Uploads */}
              <div className="lg:col-span-3 space-y-6">
                {!currentEmployee ? (
                  <div className="bg-[#121212] border border-[#27272A] border-dashed p-16 text-center text-gray-400 rounded-sm">
                    <HardHat className="w-16 h-16 mx-auto mb-6 opacity-30 text-gray-400" />
                    <p className="text-xl font-bold uppercase tracking-wide">No Uploader Selected</p>
                    <p className="text-sm text-gray-500 mt-2">Register or select a team member from the sidebar to view their shop floor uploads.</p>
                  </div>
                ) : (
                  <>
                    {/* Toolbar & Filter Header */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-[#121212] border border-[#27272A] p-4 rounded-sm">
                      <div>
                        <h3 className="text-xl sm:text-2xl font-bold text-white uppercase tracking-tight flex items-center gap-2 flex-wrap">
                          <span>Uploads By:</span>
                          <span className="text-[#FF9900]">{formatEmployeeDisplayName(currentEmployee)}</span>
                          <span className="text-base text-gray-500 normal-case font-normal">
                            ({teamMembers.find(m => m.username.replace('_ei', '') === currentEmployee)?.username || currentEmployee})
                          </span>
                        </h3>
                        <p className="text-sm text-gray-400 mt-1">
                          Showing {sortedItems.length} {sortedItems.length === 1 ? 'image' : 'images'}
                        </p>
                      </div>

                    <div className="flex flex-wrap items-center gap-4">
                      {/* Delete Selected Button */}
                      <button
                        onClick={handleDeleteSelectedUploads}
                        disabled={selectedUploads.length === 0}
                        className="flex items-center gap-2 bg-red-900 hover:bg-red-800 text-white border border-red-800 px-4 py-1.5 text-base sm:text-sm font-bold uppercase rounded-sm transition-all duration-300 disabled:opacity-30 disabled:cursor-not-allowed h-[38px] shadow-sm"
                      >
                        <Trash2 className="w-4 h-4" />
                        <span>DELETE SELECTED {selectedUploads.length > 0 && `(${selectedUploads.length})`}</span>
                      </button>

                      {/* Sorting Controls */}
                      <div className="flex items-center bg-[#1A1A1A] border border-[#27272A] rounded-sm p-1">
                        <button
                          onClick={() => setEmployeeSortOrder('desc')}
                          className={`px-3 py-1.5 text-base font-bold uppercase tracking-wider transition-all duration-300 rounded-sm flex items-center gap-1.5 ${
                            employeeSortOrder === 'desc'
                              ? 'bg-[#FF9900] text-black'
                              : 'text-gray-400 hover:text-white'
                          }`}
                          title="Newest Uploads First"
                        >
                          <ChevronDown className="w-3.5 h-3.5" />
                          Newest
                        </button>
                        <button
                          onClick={() => setEmployeeSortOrder('asc')}
                          className={`px-3 py-1.5 text-base font-bold uppercase tracking-wider transition-all duration-300 rounded-sm flex items-center gap-1.5 ${
                            employeeSortOrder === 'asc'
                              ? 'bg-[#FF9900] text-black'
                              : 'text-gray-400 hover:text-white'
                          }`}
                          title="Oldest Uploads First"
                        >
                          <ChevronUp className="w-3.5 h-3.5" />
                          Oldest
                        </button>
                      </div>

                      {/* Layout View Toggles */}
                      <div className="flex items-center bg-[#1A1A1A] border border-[#27272A] rounded-sm p-1">
                        <button
                          onClick={() => setEmployeeViewMode('grid')}
                          className={`p-1.5 transition-all duration-300 rounded-sm ${
                            employeeViewMode === 'grid'
                              ? 'bg-[#FF9900] text-black'
                              : 'text-gray-400 hover:text-white'
                          }`}
                          title="Grid View"
                        >
                          <LayoutGrid className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setEmployeeViewMode('table')}
                          className={`p-1.5 transition-all duration-300 rounded-sm ${
                            employeeViewMode === 'table'
                              ? 'bg-[#FF9900] text-black'
                              : 'text-gray-400 hover:text-white'
                          }`}
                          title="Table List View"
                        >
                          <List className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Upload List View Renderers */}
                  {sortedItems.length === 0 ? (
                    <div className="bg-[#121212] border border-[#27272A] border-dashed p-12 text-center text-gray-500 rounded-sm">
                      <HardHat className="w-8 h-8 mx-auto mb-3 opacity-30 text-gray-400" />
                      <p className="text-sm font-bold uppercase tracking-wide">No Uploads</p>
                      <p className="text-base text-gray-600 mt-1">This uploader doesn't have any images matching the filter.</p>
                    </div>
                  ) : employeeViewMode === 'grid' ? (
                    /* Image Grid View */
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
                      {sortedItems.map(item => {
                        const isSelected = selectedUploads.includes(item.id);
                        return (
                          <div
                            key={item.id}
                            className={`relative bg-[#0a0a0a] border overflow-hidden flex flex-col group transition-all duration-300 rounded-sm ${isSelected ? 'border-[#FF9900] ring-1 ring-[#FF9900]/20' : 'border-[#27272A]'}`}
                          >
                            {/* Checkbox Trigger Top Left */}
                            <label className="absolute top-3 left-3 z-10 flex items-center justify-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleToggleSelectUpload(item.id)}
                                className="peer sr-only"
                              />
                              <div className="w-6 h-6 border-2 border-white/60 bg-[#121212]/80 hover:border-[#FF9900] peer-checked:border-[#FF9900] peer-checked:bg-[#FF9900] flex items-center justify-center transition-all duration-300 rounded-sm shadow-md">
                                <Check className="w-4 h-4 text-black font-black opacity-0 peer-checked:opacity-100 transition-opacity" />
                              </div>
                            </label>

                            {/* View Button next to Checkbox */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setActiveZoomImage(item.image_url);
                              }}
                              className="absolute top-3 left-12 z-10 flex items-center gap-1 bg-[#121212]/85 border border-white/10 hover:border-[#FF9900] hover:bg-[#FF9900] hover:text-black text-white text-[9px] font-extrabold px-2.5 py-1 transition-all duration-300 rounded-sm shadow-md"
                              title="View Image Details"
                            >
                              <Eye className="w-3.5 h-3.5" />
                              VIEW
                            </button>

                            {/* Delete Draft Button Top Right */}
                            <button
                              onClick={() => handleDeleteEmployeeUpload(item)}
                              className="absolute top-3 right-3 z-10 bg-red-950/85 border border-red-900/60 p-2 hover:bg-red-650 hover:text-white transition-all duration-300 rounded-sm opacity-0 group-hover:opacity-100 shadow-md"
                              title="Delete Draft"
                            >
                              <Trash2 className="w-4 h-4 text-red-400" />
                            </button>

                            {/* Image Frame */}
                            <div className="relative aspect-video bg-[#0d0d0d] overflow-hidden cursor-pointer" onClick={() => handleToggleSelectUpload(item.id)}>
                              <img src={item.image_url} alt={item.product_name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                            </div>

                            {/* Footer Content */}
                            <div className="p-4 flex-1 flex flex-col justify-between bg-[#121212] border-t border-[#1C1C1E]">
                              <div>
                                {editingUploadId === item.id ? (
                                  <div className="flex gap-2 items-center mb-2">
                                    <input
                                      type="text"
                                      value={editUploadNameValue}
                                      onChange={(e) => setEditUploadNameValue(e.target.value)}
                                      className="flex-1 bg-[#1E1E1E] border border-[#FF9900] text-base px-2 py-1 text-white focus:outline-none uppercase"
                                      placeholder="Enter name"
                                      autoFocus
                                    />
                                    <button
                                      onClick={() => handleSaveUploadName(item.id)}
                                      className="bg-green-800 text-white p-1 hover:bg-green-650 transition-colors"
                                      title="Save Name"
                                    >
                                      <Check className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                      onClick={() => setEditingUploadId(null)}
                                      className="bg-gray-800 text-gray-400 p-1 hover:bg-gray-700 transition-colors"
                                      title="Cancel"
                                    >
                                      <X className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                ) : (
                                  <div className="flex items-center justify-between gap-2 mb-2">
                                    <h4 className="text-sm font-bold text-white truncate uppercase tracking-tight">
                                      {item.product_name || <span className="text-gray-600 italic normal-case">Unnamed Draft</span>}
                                    </h4>
                                    <div className="flex items-center gap-1.5 flex-shrink-0">
                                      <button
                                        onClick={() => {
                                          setEditingUploadId(item.id);
                                          setEditUploadNameValue(item.product_name || '');
                                        }}
                                        className="text-gray-500 hover:text-[#FF9900] transition-colors p-1"
                                        title="Edit Draft Name"
                                      >
                                        <Edit2 className="w-3.5 h-3.5" />
                                      </button>
                                      {item.product_id ? (
                                        <span className="bg-green-950/40 text-green-400 text-[9px] font-bold px-2.5 py-0.5 border border-green-900/30 rounded-full tracking-wider uppercase">
                                          Live
                                        </span>
                                      ) : (
                                        <span className="bg-gray-800/40 text-gray-400 text-[9px] font-bold px-2.5 py-0.5 border border-gray-700/30 rounded-full tracking-wider uppercase">
                                          Draft
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                )}
                                <span className="text-[10px] text-gray-500 font-bold uppercase tracking-wider block">
                                  Uploaded: {new Date(item.created_at).toLocaleDateString(undefined, {
                                    month: 'short', day: 'numeric', year: 'numeric'
                                  })}
                                </span>
                                {item.production_time !== null && item.production_time !== undefined && item.production_time !== '' && (
                                  <div className="mt-2 flex items-center gap-1.5">
                                    <span className="text-[9px] uppercase font-bold tracking-wider text-gray-400">Est. Time:</span>
                                    <span className="text-[10px] font-black text-[#FF9900] bg-[#FF9900]/10 border border-[#FF9900]/25 px-2.5 py-0.5 rounded-sm">
                                      {item.production_time} {parseInt(item.production_time) === 1 ? 'Day' : 'Days'}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    /* Table List View */
                    <div className="overflow-x-auto bg-[#121212] border border-[#27272A] rounded-sm">
                      <table className="w-full text-left border-collapse text-sm">
                        <thead>
                          <tr className="border-b border-[#27272A] bg-[#171717]">
                            <th className="p-4 w-12 text-center">
                              {/* Master select for current employee visible items */}
                              <label className="flex items-center justify-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={sortedItems.every(item => selectedUploads.includes(item.id))}
                                  onChange={(e) => {
                                    const allChecked = e.target.checked;
                                    const visibleIds = sortedItems.map(item => item.id);
                                    if (allChecked) {
                                      // Add all visible IDs that are not already selected
                                      setSelectedUploads(prev => [...new Set([...prev, ...visibleIds])]);
                                    } else {
                                      // Remove all visible IDs
                                      setSelectedUploads(prev => prev.filter(id => !visibleIds.includes(id)));
                                    }
                                  }}
                                  className="peer sr-only"
                                />
                                <div className="w-4 h-4 border border-white/60 bg-[#121212] hover:border-[#FF9900] peer-checked:border-[#FF9900] peer-checked:bg-[#FF9900] flex items-center justify-center transition-all duration-300 rounded-sm">
                                  <Check className="w-3 h-3 text-black font-black opacity-0 peer-checked:opacity-100 transition-opacity" />
                                </div>
                              </label>
                            </th>
                            <th className="p-4 font-extrabold text-base text-gray-400 uppercase tracking-wider">Thumbnail</th>
                            <th className="p-4 font-extrabold text-base text-gray-400 uppercase tracking-wider">Product Name / Draft Title</th>
                            <th className="p-4 font-extrabold text-base text-gray-400 uppercase tracking-wider">Upload Date</th>
                            <th className="p-4 font-extrabold text-base text-gray-400 uppercase tracking-wider">Est. Time</th>
                            <th className="p-4 font-extrabold text-base text-gray-400 uppercase tracking-wider">Status</th>
                            <th className="p-4 font-extrabold text-base text-gray-400 uppercase tracking-wider text-right">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedItems.map(item => {
                            const isSelected = selectedUploads.includes(item.id);
                            return (
                              <tr
                                key={item.id}
                                className={`border-b border-[#1C1C1E] hover:bg-[#161616] transition-colors ${
                                  isSelected ? 'bg-[#FF9900]/5' : ''
                                }`}
                              >
                                <td className="p-4 text-center">
                                  <label className="flex items-center justify-center cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={() => handleToggleSelectUpload(item.id)}
                                      className="peer sr-only"
                                    />
                                    <div className="w-4 h-4 border border-white/60 bg-[#121212] hover:border-[#FF9900] peer-checked:border-[#FF9900] peer-checked:bg-[#FF9900] flex items-center justify-center transition-all duration-300 rounded-sm">
                                      <Check className="w-3 h-3 text-black font-black opacity-0 peer-checked:opacity-100 transition-opacity" />
                                    </div>
                                  </label>
                                </td>
                                <td className="p-4">
                                  <div 
                                    onClick={() => setActiveZoomImage(item.image_url)}
                                    className="w-16 h-10 bg-[#0d0d0d] overflow-hidden border border-[#27272A] rounded-sm cursor-zoom-in relative group/thumb"
                                    title="Click to Zoom Image"
                                  >
                                    <img src={item.image_url} alt="" className="w-full h-full object-cover group-hover/thumb:scale-110 transition-transform duration-300" />
                                    <div className="absolute inset-0 bg-black/45 opacity-0 group-hover/thumb:opacity-100 transition-opacity flex items-center justify-center">
                                      <Eye className="w-4 h-4 text-white" />
                                    </div>
                                  </div>
                                </td>
                                <td className="p-4">
                                  {editingUploadId === item.id ? (
                                    <div className="flex gap-2 items-center max-w-xs">
                                      <input
                                        type="text"
                                        value={editUploadNameValue}
                                        onChange={(e) => setEditUploadNameValue(e.target.value)}
                                        className="flex-1 bg-[#1E1E1E] border border-[#FF9900] text-base px-2 py-1 text-white focus:outline-none uppercase"
                                        placeholder="Enter name"
                                        autoFocus
                                      />
                                      <button
                                        onClick={() => handleSaveUploadName(item.id)}
                                        className="bg-green-800 text-white p-1 hover:bg-green-650 transition-colors"
                                        title="Save Name"
                                      >
                                        <Check className="w-3.5 h-3.5" />
                                      </button>
                                      <button
                                        onClick={() => setEditingUploadId(null)}
                                        className="bg-gray-800 text-gray-400 p-1 hover:bg-gray-700 transition-colors"
                                        title="Cancel"
                                      >
                                        <X className="w-3.5 h-3.5" />
                                      </button>
                                    </div>
                                  ) : (
                                    <div className="flex items-center gap-2 group/tbl">
                                      <span className="font-bold text-white uppercase tracking-tight">
                                        {item.product_name || <span className="text-gray-655 italic normal-case">Unnamed Draft</span>}
                                      </span>
                                      <button
                                        onClick={() => {
                                          setEditingUploadId(item.id);
                                          setEditUploadNameValue(item.product_name || '');
                                        }}
                                        className="text-gray-500 hover:text-[#FF9900] transition-colors p-1 opacity-0 group-hover/tbl:opacity-100"
                                        title="Edit Draft Name"
                                      >
                                        <Edit2 className="w-3.5 h-3.5" />
                                      </button>
                                    </div>
                                  )}
                                </td>
                                <td className="p-4 text-base font-bold text-gray-400 uppercase tracking-wider">
                                  {new Date(item.created_at).toLocaleDateString(undefined, {
                                    month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
                                  })}
                                </td>
                                <td className="p-4">
                                  {item.production_time !== null && item.production_time !== undefined && item.production_time !== '' ? (
                                    <span className="text-base font-bold text-[#FF9900] bg-[#FF9900]/10 border border-[#FF9900]/20 px-2.5 py-0.5 rounded-sm">
                                      {item.production_time} {parseInt(item.production_time) === 1 ? 'Day' : 'Days'}
                                    </span>
                                  ) : (
                                    <span className="text-gray-600 text-base italic">-</span>
                                  )}
                                </td>
                                <td className="p-4">
                                  {item.product_id ? (
                                    <span className="bg-green-950/40 text-green-400 text-[9px] font-bold px-2.5 py-0.5 border border-green-900/30 rounded-full tracking-wider uppercase">
                                      Live
                                    </span>
                                  ) : (
                                    <span className="bg-gray-800/40 text-gray-400 text-[9px] font-bold px-2.5 py-0.5 border border-gray-700/30 rounded-full tracking-wider uppercase">
                                      Draft
                                    </span>
                                  )}
                                </td>
                                <td className="p-4 text-right">
                                  <div className="flex items-center justify-end gap-2">
                                    <button
                                      onClick={() => {
                                        // Promote single row by selecting it and triggering modal
                                        setSelectedUploads([item.id]);
                                        setPromotionsQueue([item]);
                                        const singleTimeVal = item.production_time !== undefined ? item.production_time : item.productionTime;
                                        setProductForm({
                                          name: item.product_name || '',
                                          description: '',
                                          image_url: item.image_url,
                                          production_time: singleTimeVal !== null && singleTimeVal !== undefined && singleTimeVal !== '' ? singleTimeVal.toString() : ''
                                        });
                                        setShowProductModal(true);
                                      }}
                                      className="px-3 py-1 bg-[#1A1A1A] hover:bg-[#FF9900] text-gray-400 hover:text-black border border-[#27272A] hover:border-transparent text-base font-bold uppercase tracking-wider rounded-sm transition-all duration-300"
                                    >
                                      Promote
                                    </button>
                                    <button
                                      onClick={() => handleDeleteEmployeeUpload(item)}
                                      className="p-1.5 bg-red-950/40 border border-red-900/30 text-red-400 hover:bg-red-900 hover:text-white rounded-sm transition-all duration-300"
                                      title="Delete"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          </div>
        )}

        {/* RFQ Leads and Conversion Stats tabs removed */}

      </div>

      {showProductModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[#121212] border border-[#27272A] p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-[#FF9900] uppercase">
                {editingProduct ? 'Edit Product' : (promotionsQueue.length > 0 ? 'Add Product (Promote Uploads)' : 'Add Product')}
              </h3>
              <button onClick={handleCloseProductModal} className="text-gray-400 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* STANDARD SINGLE PRODUCT FORM */}
            <form onSubmit={editingProduct ? handleUpdateProduct : handleCreateProduct} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">Product Name *</label>
                <input
                  type="text"
                  required
                  value={productForm.name}
                  onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                  data-testid="product-name-input"
                  className="w-full bg-[#1E1E1E] border border-[#27272A] text-[#F3F4F6] px-4 py-3 focus:border-[#FF9900] focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">Description</label>
                <textarea
                  rows={4}
                  value={productForm.description}
                  onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                  data-testid="product-description-input"
                  className="w-full bg-[#1E1E1E] border border-[#27272A] text-[#F3F4F6] px-4 py-3 focus:border-[#FF9900] focus:outline-none"
                ></textarea>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">Production time</label>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    min="0"
                    value={productForm.production_time}
                    onChange={(e) => setProductForm({ ...productForm, production_time: e.target.value })}
                    placeholder="e.g. 5"
                    className="w-32 bg-[#1E1E1E] border border-[#27272A] text-[#F3F4F6] px-4 py-3 focus:border-[#FF9900] focus:outline-none"
                  />
                  <span className="text-[#FF9900] font-bold uppercase tracking-wider text-sm">Day</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">Product Images</label>
                
                {/* Grouped Multiple Images Box */}
                {(() => {
                  const imageUrls = productForm.image_url ? productForm.image_url.split(',').map(x => x.trim()).filter(Boolean) : [];
                  if (imageUrls.length === 0) return null;
                  return (
                    <div className="mb-3 grid grid-cols-2 sm:grid-cols-3 gap-3 bg-[#0A0A0A] p-3 border border-[#27272A] rounded-sm max-h-60 overflow-y-auto">
                      {imageUrls.map((url, idx) => (
                        <div key={idx} className="relative aspect-video bg-[#121212] border border-[#27272A] group overflow-hidden rounded-sm">
                          <img src={url} alt={`Preview ${idx + 1}`} className="w-full h-full object-cover" />
                          <button
                            type="button"
                            onClick={() => {
                              const updated = imageUrls.filter((_, i) => i !== idx).join(',');
                              setProductForm({ ...productForm, image_url: updated });
                            }}
                            className="absolute top-1 right-1 bg-red-950/80 border border-red-900/60 p-1 hover:bg-red-650 text-red-400 hover:text-white transition-all rounded-sm opacity-0 group-hover:opacity-100 z-10"
                            title="Remove Image"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  );
                })()}
                
                <DragDropImageUpload
                  id="product-image-upload"
                  value=""
                  onChange={(url) => {
                    const current = productForm.image_url ? productForm.image_url.split(',').map(x => x.trim()).filter(Boolean) : [];
                    setProductForm({ ...productForm, image_url: [...current, url].join(',') });
                  }}
                  onUpload={(fileOrFiles) => handleImageUpload(fileOrFiles, 'product', (uploadedUrls) => {
                    const current = productForm.image_url ? productForm.image_url.split(',').map(x => x.trim()).filter(Boolean) : [];
                    const nextUrls = Array.isArray(uploadedUrls) ? uploadedUrls : [uploadedUrls];
                    setProductForm({ ...productForm, image_url: [...current, ...nextUrls].join(',') });
                  })}
                  disabled={uploadingProductImage}
                  multiple={true}
                  buttonText="Upload Image"
                />
                <p className="text-base text-gray-500 mt-1">Upload product images (supports multiple selection) or enter URL. Max 10MB.</p>
              </div>

              <div className="flex gap-4 pt-2">
                <button
                  type="submit"
                  data-testid="product-submit-button"
                  className="flex-1 bg-[#FF9900] text-black px-6 py-3 font-bold uppercase hover:bg-white transition-colors"
                >
                  {editingProduct ? 'Update Product' : (promotionsQueue.length > 0 ? 'Create & Promote' : 'Create Product')}
                </button>
                <button
                  type="button"
                  onClick={handleCloseProductModal}
                  className="px-6 py-3 bg-[#1E1E1E] text-gray-400 font-bold uppercase hover:bg-[#27272A] transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {showServiceModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[#121212] border border-[#27272A] p-8 max-w-2xl w-full"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-[#FF9900] uppercase">
                {editingService ? 'Edit Service' : 'Add Service'}
              </h3>
              <button onClick={() => setShowServiceModal(false)} className="text-gray-400 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={editingService ? handleUpdateService : handleCreateService} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">Service Name *</label>
                <input
                  type="text"
                  required
                  value={serviceForm.name}
                  onChange={(e) => setServiceForm({ ...serviceForm, name: e.target.value })}
                  data-testid="service-name-input"
                  className="w-full bg-[#1E1E1E] border border-[#27272A] text-[#F3F4F6] px-4 py-3 focus:border-[#FF9900] focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">Description</label>
                <textarea
                  rows={4}
                  value={serviceForm.description}
                  onChange={(e) => setServiceForm({ ...serviceForm, description: e.target.value })}
                  data-testid="service-description-input"
                  className="w-full bg-[#1E1E1E] border border-[#27272A] text-[#F3F4F6] px-4 py-3 focus:border-[#FF9900] focus:outline-none"
                ></textarea>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">Icon</label>
                <div className="space-y-3">
                  <div className="grid grid-cols-5 gap-3">
                    {AVAILABLE_ICONS.map((icon) => {
                      const IconComponent = icon.component;
                      const isSelected = serviceForm.icon === icon.name;
                      return (
                        <button
                          key={icon.name}
                          type="button"
                          onClick={() => setServiceForm({ ...serviceForm, icon: icon.name })}
                          className={`flex flex-col items-center justify-center p-4 border-2 transition-all hover:border-[#FF9900] ${
                            isSelected 
                              ? 'border-[#FF9900] bg-[#FF9900]/10' 
                              : 'border-[#27272A] bg-[#121212]'
                          }`}
                          title={icon.label}
                        >
                          <IconComponent className={`w-6 h-6 mb-2 ${isSelected ? 'text-[#FF9900]' : 'text-gray-400'}`} />
                          <span className={`text-base ${isSelected ? 'text-[#FF9900]' : 'text-gray-500'}`}>
                            {icon.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-base text-gray-500">Select an icon that represents this service</p>
                </div>
              </div>

              <div className="flex gap-4">
                <button
                  type="submit"
                  data-testid="service-submit-button"
                  className="flex-1 bg-[#FF9900] text-black px-6 py-3 font-bold uppercase hover:bg-white transition-colors"
                >
                  {editingService ? 'Update' : 'Create'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    cancelPendingUploads();
                    setShowServiceModal(false);
                  }}
                  className="px-6 py-3 bg-[#1E1E1E] text-gray-400 font-bold uppercase hover:bg-[#27272A] transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {showSupplyModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[#121212] border border-[#27272A] p-8 max-w-2xl w-full"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-[#FF9900] uppercase">
                {editingSupply ? 'Edit Supply' : 'Add Supply'}
              </h3>
              <button onClick={() => setShowSupplyModal(false)} className="text-gray-400 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={editingSupply ? handleUpdateSupply : handleCreateSupply} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">Supply Name *</label>
                <input
                  type="text"
                  required
                  value={supplyForm.name}
                  onChange={(e) => setSupplyForm({ ...supplyForm, name: e.target.value })}
                  data-testid="supply-name-input"
                  className="w-full bg-[#1E1E1E] border border-[#27272A] text-[#F3F4F6] px-4 py-3 focus:border-[#FF9900] focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">Description</label>
                <textarea
                  rows={3}
                  value={supplyForm.description}
                  onChange={(e) => setSupplyForm({ ...supplyForm, description: e.target.value })}
                  data-testid="supply-description-input"
                  className="w-full bg-[#1E1E1E] border border-[#27272A] text-[#F3F4F6] px-4 py-3 focus:border-[#FF9900] focus:outline-none"
                ></textarea>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">Sort Order</label>
                <input
                  type="number"
                  min={0}
                  value={supplyForm.sort_order}
                  onChange={(e) => setSupplyForm({ ...supplyForm, sort_order: parseInt(e.target.value) || 0 })}
                  data-testid="supply-sort-input"
                  className="w-full bg-[#1E1E1E] border border-[#27272A] text-[#F3F4F6] px-4 py-3 focus:border-[#FF9900] focus:outline-none"
                />
                <p className="text-base text-gray-500 mt-1">Lower numbers appear first on the homepage.</p>
              </div>

              <div className="flex gap-4">
                <button
                  type="submit"
                  data-testid="supply-submit-button"
                  className="flex-1 bg-[#FF9900] text-black px-6 py-3 font-bold uppercase hover:bg-white transition-colors"
                >
                  {editingSupply ? 'Update' : 'Create'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    cancelPendingUploads();
                    setShowSupplyModal(false);
                  }}
                  className="px-6 py-3 bg-[#1E1E1E] text-gray-400 font-bold uppercase hover:bg-[#27272A] transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {showContactModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[#121212] border border-[#27272A] p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-[#FF9900] uppercase">Edit Contact Information</h3>
              <button onClick={() => setShowContactModal(false)} className="text-gray-400 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleUpdateContactInfo} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">Phone *</label>
                  <input
                    type="text"
                    required
                    value={contactForm.phone}
                    onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                    data-testid="contact-phone-input"
                    className="w-full bg-[#1E1E1E] border border-[#27272A] text-[#F3F4F6] px-4 py-3 focus:border-[#FF9900] focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">Fax</label>
                  <input
                    type="text"
                    value={contactForm.fax}
                    onChange={(e) => setContactForm({ ...contactForm, fax: e.target.value })}
                    className="w-full bg-[#1E1E1E] border border-[#27272A] text-[#F3F4F6] px-4 py-3 focus:border-[#FF9900] focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">Email *</label>
                <input
                  type="email"
                  required
                  value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                  data-testid="contact-email-input"
                  className="w-full bg-[#1E1E1E] border border-[#27272A] text-[#F3F4F6] px-4 py-3 focus:border-[#FF9900] focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">Address *</label>
                <input
                  type="text"
                  required
                  value={contactForm.address}
                  onChange={(e) => setContactForm({ ...contactForm, address: e.target.value })}
                  className="w-full bg-[#1E1E1E] border border-[#27272A] text-[#F3F4F6] px-4 py-3 focus:border-[#FF9900] focus:outline-none"
                />
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">City *</label>
                  <input
                    type="text"
                    required
                    value={contactForm.city}
                    onChange={(e) => setContactForm({ ...contactForm, city: e.target.value })}
                    className="w-full bg-[#1E1E1E] border border-[#27272A] text-[#F3F4F6] px-4 py-3 focus:border-[#FF9900] focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">Province *</label>
                  <input
                    type="text"
                    required
                    value={contactForm.province}
                    onChange={(e) => setContactForm({ ...contactForm, province: e.target.value })}
                    className="w-full bg-[#1E1E1E] border border-[#27272A] text-[#F3F4F6] px-4 py-3 focus:border-[#FF9900] focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">Postal Code *</label>
                  <input
                    type="text"
                    required
                    value={contactForm.postal_code}
                    onChange={(e) => setContactForm({ ...contactForm, postal_code: e.target.value })}
                    className="w-full bg-[#1E1E1E] border border-[#27272A] text-[#F3F4F6] px-4 py-3 focus:border-[#FF9900] focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">Country *</label>
                <input
                  type="text"
                  required
                  value={contactForm.country}
                  onChange={(e) => setContactForm({ ...contactForm, country: e.target.value })}
                  className="w-full bg-[#1E1E1E] border border-[#27272A] text-[#F3F4F6] px-4 py-3 focus:border-[#FF9900] focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">Business Hours</label>
                <textarea
                  rows={4}
                  value={contactForm.business_hours || ''}
                  onChange={(e) => setContactForm({ ...contactForm, business_hours: e.target.value })}
                  placeholder="e.g. Monday - Friday: 7:00 AM - 5:00 PM&#10;Saturday: By Appointment&#10;Sunday: Closed"
                  className="w-full bg-[#1E1E1E] border border-[#27272A] text-[#F3F4F6] px-4 py-3 focus:border-[#FF9900] focus:outline-none resize-none"
                />
              </div>

              <div className="flex gap-4">
                <button
                  type="submit"
                  data-testid="contact-submit-button"
                  className="flex-1 bg-[#FF9900] text-black px-6 py-3 font-bold uppercase hover:bg-white transition-colors"
                >
                  Update
                </button>
                <button
                  type="button"
                  onClick={() => setShowContactModal(false)}
                  className="px-6 py-3 bg-[#1E1E1E] text-gray-400 font-bold uppercase hover:bg-[#27272A] transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {showSiteSettingsModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[#121212] border border-[#27272A] p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-[#FF9900] uppercase">Edit Site Settings</h3>
              <button onClick={() => setShowSiteSettingsModal(false)} className="text-gray-400 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleUpdateSiteSettings} className="space-y-6">
              <div className="bg-[#1E1E1E] p-6 border-l-4 border-[#FF9900]">
                <h4 className="text-lg font-bold text-[#F3F4F6] mb-4 uppercase">Branding</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      Company Logo
                    </label>
                    
                    {siteSettingsForm.logo_url && (
                      <div className="mb-3 p-4 bg-[#121212] border border-[#27272A] flex items-center gap-4">
                        <img src={siteSettingsForm.logo_url} alt="Logo Preview" className="w-20 h-20 object-contain bg-white/5 p-2" />
                        <div className="flex-1">
                          <p className="text-base text-gray-400 break-all">{siteSettingsForm.logo_url}</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setSiteSettingsForm({ ...siteSettingsForm, logo_url: '' })}
                          className="text-red-400 hover:text-red-300 text-sm font-bold"
                        >
                          Remove
                        </button>
                      </div>
                    )}
                    
                    <DragDropImageUpload
                      id="logo-upload"
                      value={siteSettingsForm.logo_url}
                      onChange={(url) => setSiteSettingsForm({ ...siteSettingsForm, logo_url: url })}
                      onUpload={handleLogoUpload}
                      disabled={uploadingLogo}
                      placeholder="Or enter URL: https://example.com/logo.png"
                      buttonText="Upload Logo"
                    />
                    <p className="text-base text-gray-500 mt-2">Upload an image or enter a URL. Leave empty to use default icon. Max 5MB.</p>
                  </div>

                  {/* Hero Logo Appearance Controls */}
                  <div className="bg-[#121212] border border-[#27272A] p-4 rounded">
                    <h5 className="text-sm font-bold text-[#FF9900] uppercase mb-4">Hero Logo Appearance</h5>

                    {/* Live Logo Preview */}
                    {siteSettingsForm.logo_url && (
                      <div className="flex justify-center mb-4">
                        <img
                          src={siteSettingsForm.logo_url}
                          alt="Live Preview"
                          style={{
                            width: `${siteSettingsForm.logo_size || 80}px`,
                            height: `${siteSettingsForm.logo_size || 80}px`,
                            objectFit: 'contain',
                            filter: (siteSettingsForm.logo_glow_intensity || 0) > 0
                              ? `drop-shadow(0 0 ${siteSettingsForm.logo_glow_intensity}px ${siteSettingsForm.logo_glow_color || '#FF9900'})`
                              : 'none',
                            transition: 'all 0.2s ease'
                          }}
                        />
                      </div>
                    )}

                    {/* Size Slider */}
                    <div className="mb-4">
                      <div className="flex justify-between items-center mb-1">
                        <label className="text-sm font-medium text-gray-400">Logo Size</label>
                        <span className="text-sm text-[#FF9900] font-mono">{siteSettingsForm.logo_size || 80}px</span>
                      </div>
                      <input
                        type="range" min="40" max="200" step="4"
                        value={siteSettingsForm.logo_size || 80}
                        onChange={(e) => setSiteSettingsForm({ ...siteSettingsForm, logo_size: parseInt(e.target.value) })}
                        className="w-full accent-[#FF9900] cursor-pointer"
                      />
                      <div className="flex justify-between text-base text-gray-600 mt-1">
                        <span>Small (40px)</span><span>Large (200px)</span>
                      </div>
                    </div>

                    {/* Glow Intensity Slider */}
                    <div className="mb-4">
                      <div className="flex justify-between items-center mb-1">
                        <label className="text-sm font-medium text-gray-400">Glow Intensity</label>
                        <span className="text-sm text-[#FF9900] font-mono">
                          {(siteSettingsForm.logo_glow_intensity || 0) === 0 ? 'Off' : `${siteSettingsForm.logo_glow_intensity}px`}
                        </span>
                      </div>
                      <input
                        type="range" min="0" max="40" step="1"
                        value={siteSettingsForm.logo_glow_intensity || 0}
                        onChange={(e) => setSiteSettingsForm({ ...siteSettingsForm, logo_glow_intensity: parseInt(e.target.value) })}
                        className="w-full accent-[#FF9900] cursor-pointer"
                      />
                      <div className="flex justify-between text-base text-gray-600 mt-1">
                        <span>Off</span><span>Max Glow</span>
                      </div>
                    </div>

                    {/* Glow Color Picker */}
                    {(siteSettingsForm.logo_glow_intensity || 0) > 0 && (
                      <div>
                        <label className="text-sm font-medium text-gray-400 mb-2 block">Glow Color</label>
                        <div className="flex items-center gap-3">
                          <input
                            type="color"
                            value={siteSettingsForm.logo_glow_color || '#FF9900'}
                            onChange={(e) => setSiteSettingsForm({ ...siteSettingsForm, logo_glow_color: e.target.value })}
                            className="w-12 h-10 rounded cursor-pointer border border-[#27272A] bg-transparent"
                          />
                          <input
                            type="text"
                            value={siteSettingsForm.logo_glow_color || '#FF9900'}
                            onChange={(e) => setSiteSettingsForm({ ...siteSettingsForm, logo_glow_color: e.target.value })}
                            className="flex-1 bg-[#121212] border border-[#27272A] text-[#F3F4F6] px-3 py-2 text-sm font-mono focus:border-[#FF9900] focus:outline-none"
                            placeholder="#FF9900"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">Company Name *</label>
                      <input
                        type="text"
                        required
                        value={siteSettingsForm.company_name}
                        onChange={(e) => setSiteSettingsForm({ ...siteSettingsForm, company_name: e.target.value })}
                        className="w-full bg-[#121212] border border-[#27272A] text-[#F3F4F6] px-4 py-3 focus:border-[#FF9900] focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">Tagline *</label>
                      <input
                        type="text"
                        required
                        value={siteSettingsForm.tagline}
                        onChange={(e) => setSiteSettingsForm({ ...siteSettingsForm, tagline: e.target.value })}
                        className="w-full bg-[#121212] border border-[#27272A] text-[#F3F4F6] px-4 py-3 focus:border-[#FF9900] focus:outline-none"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-[#1E1E1E] p-6 border-l-4 border-[#FF9900]">
                <h4 className="text-lg font-bold text-[#F3F4F6] mb-4 uppercase">Images</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">Hero Background Image *</label>
                    
                    {siteSettingsForm.hero_image_url && (
                      <div className="mb-3">
                        <img src={siteSettingsForm.hero_image_url} alt="Hero Preview" className="w-full h-32 object-cover border-2 border-[#27272A]" />
                      </div>
                    )}
                    
                    <DragDropImageUpload
                      id="hero-image-upload"
                      value={siteSettingsForm.hero_image_url}
                      onChange={(url) => setSiteSettingsForm({ ...siteSettingsForm, hero_image_url: url })}
                      onUpload={(file) => handleImageUpload(file, 'hero', (url) => setSiteSettingsForm({ ...siteSettingsForm, hero_image_url: url }))}
                      disabled={uploadingHeroImage}
                      placeholder="Or enter URL"
                      buttonText="Upload Hero"
                    />
                    <p className="text-base text-gray-500 mt-1">Upload an image or enter URL. Max 10MB.</p>

                    {/* New Hero Image Appearance Section */}
                    <div className="mt-6 p-4 bg-[#121212] border border-[#27272A] rounded">
                      <h5 className="text-sm font-bold text-[#FF9900] uppercase mb-4">Hero Image Appearance</h5>
                      
                      {/* Image Opacity Slider */}
                      <div className="mb-4">
                        <div className="flex justify-between items-center mb-1">
                          <label className="text-sm font-medium text-gray-400">Image Opacity</label>
                          <span className="text-sm text-[#FF9900] font-mono">{siteSettingsForm.hero_image_opacity ?? 40}%</span>
                        </div>
                        <input
                          type="range" min="0" max="100" step="5"
                          value={siteSettingsForm.hero_image_opacity ?? 40}
                          onChange={(e) => setSiteSettingsForm({ ...siteSettingsForm, hero_image_opacity: parseInt(e.target.value) })}
                          className="w-full accent-[#FF9900] cursor-pointer"
                        />
                        <div className="flex justify-between text-base text-gray-600 mt-1">
                          <span>0% (Dark)</span><span>100% (Full)</span>
                        </div>
                      </div>

                      {/* Gradient Opacity Slider */}
                      <div>
                        <div className="flex justify-between items-center mb-1">
                          <label className="text-sm font-medium text-gray-400">Gradient Intensity</label>
                          <span className="text-sm text-[#FF9900] font-mono">
                            {(siteSettingsForm.hero_gradient_opacity ?? 60) === 0 ? 'Off' : `${siteSettingsForm.hero_gradient_opacity}%`}
                          </span>
                        </div>
                        <input
                          type="range" min="0" max="100" step="5"
                          value={siteSettingsForm.hero_gradient_opacity ?? 60}
                          onChange={(e) => setSiteSettingsForm({ ...siteSettingsForm, hero_gradient_opacity: parseInt(e.target.value) })}
                          className="w-full accent-[#FF9900] cursor-pointer"
                        />
                        <div className="flex justify-between text-base text-gray-600 mt-1">
                          <span>Safe (0%)</span><span>Strong (100%)</span>
                        </div>
                        <p className="text-[10px] text-gray-500 mt-2 italic">Stronger gradients help white text stand out on busy images.</p>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">About Section Image *</label>
                    
                    {siteSettingsForm.about_image_url && (
                      <div className="mb-3">
                        <img src={siteSettingsForm.about_image_url} alt="About Preview" className="w-full h-32 object-cover border-2 border-[#27272A]" />
                      </div>
                    )}
                    
                    <DragDropImageUpload
                      id="about-image-upload"
                      value={siteSettingsForm.about_image_url}
                      onChange={(url) => setSiteSettingsForm({ ...siteSettingsForm, about_image_url: url })}
                      onUpload={(file) => handleImageUpload(file, 'about', (url) => setSiteSettingsForm({ ...siteSettingsForm, about_image_url: url }))}
                      disabled={uploadingAboutImage}
                      placeholder="Or enter URL"
                      buttonText="Upload About"
                    />
                    <p className="text-base text-gray-500 mt-1">Upload an image or enter URL. Max 10MB.</p>
                  </div>
                </div>
              </div>

              <div className="bg-[#1E1E1E] p-6 border-l-4 border-[#FF9900]">
                <h4 className="text-lg font-bold text-[#F3F4F6] mb-4 uppercase">About Section</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">About Title *</label>
                    <input
                      type="text"
                      required
                      value={siteSettingsForm.about_title}
                      onChange={(e) => setSiteSettingsForm({ ...siteSettingsForm, about_title: e.target.value })}
                      className="w-full bg-[#121212] border border-[#27272A] text-[#F3F4F6] px-4 py-3 focus:border-[#FF9900] focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">About Content *</label>
                    <textarea
                      rows={8}
                      required
                      value={siteSettingsForm.about_content}
                      onChange={(e) => setSiteSettingsForm({ ...siteSettingsForm, about_content: e.target.value })}
                      data-testid="about-content-input"
                      className="w-full bg-[#121212] border border-[#27272A] text-[#F3F4F6] px-4 py-3 focus:border-[#FF9900] focus:outline-none"
                      placeholder="Enter your company's about section content..."
                    ></textarea>
                    <p className="text-base text-gray-500 mt-1">Use line breaks for paragraphs</p>
                  </div>
                </div>
              </div>

              <div className="flex gap-4">
                <button
                  type="submit"
                  data-testid="site-settings-submit-button"
                  className="flex-1 bg-[#FF9900] text-black px-6 py-3 font-bold uppercase hover:bg-white transition-colors"
                >
                  Update Settings
                </button>
                <button
                  type="button"
                  onClick={() => setShowSiteSettingsModal(false)}
                  className="px-6 py-3 bg-[#1E1E1E] text-gray-400 font-bold uppercase hover:bg-[#27272A] transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
      {/* FULLSCREEN LIGHTBOX MODAL */}
      <AnimatePresence>
        {activeZoomImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActiveZoomImage(null)}
            className="fixed inset-0 bg-black/90 backdrop-blur-md z-50 flex flex-col items-center justify-center p-4 cursor-zoom-out"
          >
            {/* Close Button */}
            <button
              onClick={() => setActiveZoomImage(null)}
              className="absolute top-4 right-4 bg-[#121212] border border-[#27272A] p-2 hover:bg-white hover:text-black transition-colors rounded-sm text-gray-400 z-50 shadow-md"
            >
              <X className="w-6 h-6" />
            </button>
            
            {/* Image Frame */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="relative max-w-5xl w-full h-[75vh] flex items-center justify-center p-4 overflow-hidden select-none"
            >
              <img
                src={activeZoomImage}
                alt="Zoomed view"
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleMouseUp}
                style={{
                  transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoomScale})`,
                  cursor: zoomScale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'zoom-in',
                  transition: isDragging ? 'none' : 'transform 0.2s cubic-bezier(0.16, 1, 0.3, 1)'
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (zoomScale === 1) {
                    setZoomScale(2);
                  } else {
                    setZoomScale(1);
                    setPanOffset({ x: 0, y: 0 });
                  }
                }}
                className="max-w-full max-h-full object-contain border border-[#27272A] rounded-sm shadow-2xl bg-[#0a0a0a]"
              />
            </motion.div>

            {/* Zoom Controls Bar */}
            <div 
              className="absolute bottom-6 bg-[#121212]/80 border border-[#27272A] backdrop-blur-md px-5 py-2.5 flex items-center gap-4 rounded-full z-50 shadow-xl select-none"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={() => {
                  setZoomScale(prev => Math.max(1, prev - 0.5));
                  if (zoomScale <= 1.5) setPanOffset({ x: 0, y: 0 });
                }}
                disabled={zoomScale <= 1}
                className="p-1 hover:text-[#FF9900] disabled:text-gray-600 disabled:hover:text-gray-600 transition-colors text-white text-base font-black uppercase tracking-wider"
                title="Zoom Out"
              >
                Zoom Out (-)
              </button>
              <span className="text-base font-black text-gray-400 select-none uppercase tracking-wider bg-black/40 border border-white/5 px-2.5 py-1 rounded-full min-w-[60px] text-center">
                {Math.round(zoomScale * 100)}%
              </span>
              <button
                type="button"
                onClick={() => setZoomScale(prev => Math.min(4, prev + 0.5))}
                disabled={zoomScale >= 4}
                className="p-1 hover:text-[#FF9900] disabled:text-gray-600 disabled:hover:text-gray-600 transition-colors text-white text-base font-black uppercase tracking-wider"
                title="Zoom In"
              >
                Zoom In (+)
              </button>
              <div className="w-px h-4 bg-[#27272A]"></div>
              <button
                type="button"
                onClick={() => {
                  setZoomScale(1);
                  setPanOffset({ x: 0, y: 0 });
                }}
                disabled={zoomScale === 1 && panOffset.x === 0 && panOffset.y === 0}
                className="p-1 text-base font-black uppercase tracking-wider hover:text-[#FF9900] disabled:text-gray-600 disabled:hover:text-gray-600 transition-colors text-white"
                title="Reset Zoom"
              >
                Reset
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminDashboard;
